import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { CheckCircle, Loader2, XCircle } from 'lucide-react';
import { subscription } from '../api';

const PayPalSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [message, setMessage] = useState('');
  
  // PayPal returns token in URL
  const token = searchParams.get('token');
  const payerId = searchParams.get('PayerID');

  useEffect(() => {
    // Try to get order_id from localStorage first (stored before redirect)
    const orderId = localStorage.getItem('paypal_order_id') || token;
    
    if (orderId && payerId) {
      capturePayment(orderId);
    } else if (orderId) {
      // Just token, try to capture anyway
      capturePayment(orderId);
    } else {
      setStatus('error');
      setMessage('No order information found. Please try again.');
    }
  }, [token, payerId]);

  const capturePayment = async (orderId) => {
    try {
      const response = await subscription.capturePayPalOrder(orderId);
      const data = response.data;

      if (data.status === 'success' || data.subscription_activated) {
        // Clear stored order_id
        localStorage.removeItem('paypal_order_id');
        setStatus('success');
        setMessage('Your premium subscription is now active!');
      } else if (data.status === 'already_captured') {
        setStatus('success');
        setMessage('Your subscription is already active!');
      } else {
        setStatus('error');
        setMessage(data.message || 'Payment capture failed. Please contact support.');
      }
    } catch (error) {
      console.error('PayPal capture error:', error);
      setStatus('error');
      setMessage(error.response?.data?.detail || 'Error processing payment. Please contact support.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 to-white flex items-center justify-center p-4">
      <Card className="max-w-md w-full shadow-xl">
        <CardContent className="p-8 text-center">
          {status === 'loading' && (
            <>
              <Loader2 className="h-16 w-16 text-yellow-600 animate-spin mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Processing PayPal Payment...</h2>
              <p className="text-gray-600">Please wait while we confirm your payment.</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="bg-green-100 rounded-full p-4 w-24 h-24 mx-auto mb-4 flex items-center justify-center">
                <CheckCircle className="h-16 w-16 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Welcome to Premium!</h2>
              <p className="text-gray-600 mb-6">{message}</p>
              <div className="space-y-3">
                <p className="text-sm text-gray-500">You now have access to:</p>
                <ul className="text-sm text-left space-y-2 bg-green-50 p-4 rounded-lg">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    Unlimited businesses
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    Unlimited invoices & expenses
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    PDF & Excel exports
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    Email reminders
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    Advanced reports
                  </li>
                </ul>
              </div>
              <Button 
                onClick={() => navigate('/dashboard')}
                className="w-full mt-6 bg-blue-600 hover:bg-blue-700"
              >
                Go to Dashboard
              </Button>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="bg-red-100 rounded-full p-4 w-24 h-24 mx-auto mb-4 flex items-center justify-center">
                <XCircle className="h-16 w-16 text-red-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Payment Issue</h2>
              <p className="text-gray-600 mb-6">{message}</p>
              <div className="space-y-3">
                <Button 
                  onClick={() => navigate('/settings')}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                >
                  Try Again
                </Button>
                <Button 
                  onClick={() => navigate('/dashboard')}
                  variant="outline"
                  className="w-full"
                >
                  Go to Dashboard
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PayPalSuccess;
